// Picker logos for the insurance selection redesign experiment. The designs
// color the brand at the start of the listed insurers' titles; every other
// insurer shows its plain title. They live in the web bundle only while the
// experiment runs.
//
// insuranceLogo is the only place that knows where logos come from. Once the
// backend serves them, it returns { kind: 'image', src, showsTitle } built from
// the insurer's API fields and the map below goes; the screen already renders
// both kinds.

type WordmarkPart = { text: string; color: string }

export type InsuranceLogo =
  // titleRest is the part of the title that follows the label.
  | { kind: 'wordmark'; wordmark: WordmarkPart[]; titleRest: string }
  | { kind: 'image'; src: string; showsTitle: boolean }

// An insurer as returned by /popular_corporates and /search_corporate.
type Insurance = { key: string; title: string }

const wordmark = (text: string, color: string): WordmarkPart[] => [
  { text, color },
]

const AOK_GREEN = '#035d3e'
const IKK_BLACK = '#1a181b'

// Keyed by the production corporate key from /popular_corporates and
// /search_corporate. Brand colors too light to read are darkened, keeping
// hue and chroma (OKLCH), just enough for WCAG AA's 4.5:1 on the row's default
// background (#edf3f7). On hover and keyboard focus the row darkens to
// #dae6f0, where those sit near 4:1; staying close to the brands came first.
const INSURER_WORDMARKS: Record<string, WordmarkPart[]> = {
  'aok-baden-wuerttemberg': wordmark('AOK', AOK_GREEN),
  'aok-bayern': wordmark('AOK', AOK_GREEN),
  'aok-bremen-bremerhaven': wordmark('AOK', AOK_GREEN),
  'aok-hessen': wordmark('AOK', AOK_GREEN),
  'aok-niedersachsen': wordmark('AOK', AOK_GREEN),
  'aok-nordost': wordmark('AOK', AOK_GREEN),
  'aok-nordwest': wordmark('AOK', AOK_GREEN),
  'aok-plus': wordmark('AOK', AOK_GREEN),
  'aok-rheinland-hamburg': wordmark('AOK', AOK_GREEN),
  'aok-rheinland-pfalz-saarland': wordmark('AOK', AOK_GREEN),
  'aok-sachsen-anhalt': wordmark('AOK', AOK_GREEN),
  'bahn-bkk': wordmark('BAHN-BKK', '#926700'),
  barmer: wordmark('BARMER', '#3c7d00'),
  'bkk-firmus': wordmark('BKK firmus', '#a75b00'),
  'bkk-mobil-oil': wordmark('Mobil Krankenkasse', '#0d4099'),
  'bkk-tui': wordmark('TUI BKK', '#df0010'),
  'bosch-bkk': wordmark('Bosch BKK', '#0074b5'),
  'dak-gesundheit': wordmark('DAK', '#c24500'),
  dkv: wordmark('DKV', '#095751'),
  // Redion, formerly Generali Deutschland Krankenversicherung.
  generalihealthsolutions: [
    { text: 'Redi', color: '#03033a' },
    { text: 'on', color: '#c11c16' },
  ],
  'heimat-krankenkasse': wordmark('Heimat', '#0074b5'),
  'hek-hanseatische-krankenkasse': wordmark('HEK', '#000000'),
  hkk: [
    { text: 'h', color: '#dc0a1e' },
    { text: 'kk', color: '#004272' },
  ],
  huk: wordmark('HUK-COBURG', '#7a7000'),
  'ikk-brandenburg-und-berlin': wordmark('IKK', IKK_BLACK),
  'ikk-classic': wordmark('IKK', IKK_BLACK),
  'ikk-gesund-plus': wordmark('IKK', IKK_BLACK),
  'ikk-nord': wordmark('IKK', IKK_BLACK),
  'ikk-suedwest': wordmark('IKK', IKK_BLACK),
  'kkh-kaufmaennische-krankenkasse': wordmark('KKH', '#000d54'),
  knappschaft: wordmark('KNAPPSCHAFT', '#c71217'),
  'mhplus-betriebskrankenkasse': wordmark('mhplus', '#41758e'),
  'novitas-bkk': wordmark('novitas bkk', '#007850'),
  sbk: wordmark('SBK', '#003c6e'),
  'securvita-krankenkasse': wordmark('SECURVITA', '#df001b'),
  'sozialversicherung-fuer-landwirtschaft--forsten-gartenbau': wordmark(
    'SVLFG',
    '#407d00',
  ),
  'tk-techniker-krankenkasse': wordmark('TK', '#0076a9'),
  'viactiv-krankenkasse': wordmark('VIACTIV', '#b35300'),
}

export const insuranceLogo = ({
  key,
  title,
}: Insurance): InsuranceLogo | null => {
  const parts = INSURER_WORDMARKS[key]
  if (!parts) return null

  // A title that doesn't start with its brand stays plain, so a renamed
  // insurer never has the wrong words colored.
  const label = parts.map((part) => part.text).join('')
  if (!title.startsWith(label)) return null

  return {
    kind: 'wordmark',
    wordmark: parts,
    titleRest: title.slice(label.length),
  }
}
